import { ChangeDetectionStrategy, Component, inject, Inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatInput } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { InfiniteScrollTriggerComponent } from '@vendasta/galaxy/infinite-scroll-trigger';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, of, switchMap, tap } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Contact, ContactSearchResult, SendPreviewDialogData } from '../interface';

@Component({
  selector: 'email-send-preview-dialog',
  templateUrl: './send-preview-dialog.component.html',
  styleUrls: ['./send-preview-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    InfiniteScrollTriggerComponent,
  ],
})
export class SendPreviewDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<SendPreviewDialogComponent>);
  private readonly snackbarService = inject(SnackbarService);

  protected readonly contactSearchFormField = new FormControl<string>('', { nonNullable: true });
  private readonly pageSize = 20;

  protected readonly hasMore = signal(false);
  private readonly cursor = signal('');
  protected readonly contacts = signal<Contact[]>([]);
  protected readonly loading = signal(true);
  protected readonly selectedContact = signal<Contact | null>(null);
  protected readonly sending = signal(false);

  private readonly loadMore$$ = new BehaviorSubject<boolean>(false);
  private readonly searchTerm$ = this.contactSearchFormField.valueChanges.pipe(
    startWith(''),
    debounceTime(500),
    distinctUntilChanged(),
    tap(() => {
      this.contacts.set([]);
      this.hasMore.set(false);
      this.cursor.set('');
    }),
  );

  constructor(@Inject(MAT_DIALOG_DATA) readonly data: SendPreviewDialogData) {
    combineLatest([this.searchTerm$, this.loadMore$$])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([searchTerm]) => {
          return this.data.contactProvider.searchContacts(searchTerm, this.cursor(), this.pageSize).pipe(
            catchError(() => {
              this.snackbarService.openErrorSnack('CONTENT_BUILDER.SEND_PREVIEW_DIALOG.ERROR.SEARCH_FAILED');
              return of({ contacts: [], nextCursor: '', hasMore: false } as ContactSearchResult);
            }),
          );
        }),
      )
      .subscribe((result) => {
        this.contacts.update((prev) => prev.concat(result.contacts));
        this.cursor.set(result.nextCursor);
        this.hasMore.set(result.hasMore);
        this.loading.set(false);
      });
  }

  protected loadMoreContacts(): void {
    if (this.hasMore()) {
      this.loadMore$$.next(true);
    }
  }

  protected selectContact(contact: Contact): void {
    this.selectedContact.set(contact);
  }

  protected backToList(): void {
    this.selectedContact.set(null);
  }

  protected async confirmSend(): Promise<void> {
    const contact = this.selectedContact();
    if (!contact) {
      return;
    }
    this.sending.set(true);
    try {
      await this.data.onSend(contact);
      this.dialogRef.close(true);
    } catch {
      this.snackbarService.openErrorSnack('CONTENT_BUILDER.SEND_PREVIEW_DIALOG.ERROR.SEND_FAILED');
    } finally {
      this.sending.set(false);
    }
  }

  protected cancel(): void {
    this.dialogRef.close(false);
  }
}
